import { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { stripMd } from "../markdown";
import { CARD_QUESTIONS } from "../mock";
import { ask } from "../sse";
import type { ChatMessage, FactKey } from "../types";
import { ChatIcon } from "./icons";

/** Corpus reads are engine internals; a pipeline dispatch is not. */
const LOOKUP = /^→ (search|fetch_by_id|get_claims|list_by|resolve_entity)/;

const PROMPTS: Array<{ key: FactKey; label: string }> = [
  { key: "leave", label: "Annual leave" },
  { key: "mc", label: "Sick leave" },
  { key: "payslip", label: "Payslip" },
];

export interface Prefill {
  text: string;
  nonce: number;
}

interface Props {
  boot: ChatMessage[];
  prefill: Prefill | null;
}

export function AskHR({ boot, prefill }: Props) {
  const [messages, setMessages] = useState<ChatMessage[]>(boot);
  const [draft, setDraft] = useState("");
  const nextId = useRef(boot.length);
  const inputRef = useRef<HTMLInputElement>(null);
  const logRef = useRef<HTMLDivElement>(null);

  const add = (role: ChatMessage["role"], text: string) => {
    const id = nextId.current++;
    setMessages((m) => [...m, { id, role, text }]);
    return id;
  };
  const patch = (id: number, text: string) =>
    setMessages((m) => m.map((msg) => (msg.id === id ? { ...msg, text } : msg)));

  // The log pins to the newest message, the way a chat should. The pin repeats
  // after paint so it still lands once the viewport-derived height has settled.
  useEffect(() => {
    const log = logRef.current;
    if (!log) return;
    const pin = () => {
      log.scrollTop = log.scrollHeight;
    };
    pin();
    const raf = requestAnimationFrame(pin);
    return () => cancelAnimationFrame(raf);
  }, [messages]);

  // A record tab handed us its question — fill the box and hand over the caret.
  useEffect(() => {
    if (!prefill) return;
    setDraft(prefill.text);
    inputRef.current?.focus();
  }, [prefill?.nonce]);

  async function submit(ev: React.FormEvent) {
    ev.preventDefault();
    const q = draft.trim();
    if (!q) return;
    setDraft("");
    add("user", q);
    const outId = add("assistant", "…");
    let lookups = 0;
    let lookupId: number | null = null;
    try {
      await ask(q, {
        onText: (a) => patch(outId, a),
        // Pipeline dispatch stays visible — it is what the answer is grounded
        // in. Corpus reads collapse into one quiet line instead of a wall of
        // → search / → fetch_by_id.
        onTool: (label) => {
          if (!LOOKUP.test(label)) {
            lookupId = null;
            add("tool", label);
            return;
          }
          lookups += 1;
          const text = `→ checked ${lookups} source${lookups === 1 ? "" : "s"}`;
          if (lookupId === null) lookupId = add("tool", text);
          else patch(lookupId, text);
        },
        onFinal: (a) => patch(outId, a),
        onError: (m) => patch(outId, "error: " + m),
      });
    } catch (e) {
      patch(outId, String(e));
    }
  }

  return (
    <div className="chat">
      <div className="chat-head">
        <div className="icon">
          <ChatIcon />
        </div>
        <b>How can HR help?</b>
      </div>

      <div className="log" ref={logRef}>
        {messages.map((m) =>
          m.role === "assistant" ? (
            <div key={m.id} className="msg assistant">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  table: ({ node, ...props }) => (
                    <div className="table-wrap">
                      <table {...props} />
                    </div>
                  ),
                  a: ({ node, ...props }) => (
                    <a {...props} target="_blank" rel="noopener" />
                  ),
                }}
              >
                {stripMd(m.text)}
              </ReactMarkdown>
            </div>
          ) : (
            <div key={m.id} className={"msg " + m.role}>
              {m.text}
            </div>
          ),
        )}
      </div>

      <div className="prompts">
        {PROMPTS.map((p) => (
          <button
            key={p.key}
            type="button"
            className="prompt"
            onClick={() => {
              setDraft(CARD_QUESTIONS[p.key]);
              inputRef.current?.focus();
            }}
          >
            {p.label}
          </button>
        ))}
      </div>

      <form onSubmit={submit}>
        <input
          ref={inputRef}
          placeholder="Ask a question"
          autoComplete="off"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
        />
        <button>Ask</button>
      </form>
    </div>
  );
}
